<!--
SubscriptionTermsConsent — the agreement a franchise signs before subscribing.

The PLAN decides which agreement applies; the reader's own language decides
only which translation is shown, falling back through the chain. Either way it
is the same version, and the block says which language it ended up showing.

Placed LAST in the subscribe form, after the plan and payment choices, because
signing is the final act: you agree to terms you have already seen the price of.

WHEN THERE IS NOTHING TO SIGN, THIS BLOCK DISAPPEARS AND DOES NOT BLOCK.
A plan whose agreement staff has not published yet is a staff omission, and
the backend deliberately lets subscribing proceed. Showing an empty box with a
disabled button here would turn that omission into a dead end for the
franchisee, which is exactly what the backend refused to do.

The signature is the last field, and the submit stays disabled until both the
checkbox and a real signature exist. The value emitted by InputSignature is
already '' for an accidental tap, so a stray touch cannot stand in for consent.
-->
<template>
  <box
    v-if="state === 'pending' || state === 'accepted' || state === 'error'"
    type="primary"
    :title="t('Termos de assinatura')"
    data-testid="subscription-terms-consent"
  >
    <div v-if="isLoading" class="consent-loading" data-testid="subscription-terms-consent-loading">
      <spinner style="width: 24px; height: 24px;" />
    </div>

    <template v-else-if="state === 'error'">
      <p class="consent-error" data-testid="subscription-terms-consent-load-error">
        <i class="pi pi-exclamation-triangle"></i>
        {{ t('Não foi possível carregar os termos de assinatura.') }}
      </p>
      <Button
        :label="t('Tentar de novo')"
        icon="pi pi-refresh"
        severity="secondary"
        size="small"
        data-testid="subscription-terms-consent-retry"
        @click="retry"
      />
    </template>

    <template v-else-if="state === 'accepted'">
      <p class="consent-accepted" data-testid="subscription-terms-consent-accepted">
        <i class="pi pi-check-circle"></i>
        {{ t('Você já assinou a versão') }} {{ termo?.versao }} {{ t('destes termos em') }}
        {{ acceptedAt }}.
      </p>
    </template>

    <template v-else>
      <p class="consent-meta" data-testid="subscription-terms-consent-meta">
        {{ termo?.titulo }} — {{ t('versão') }} {{ termo?.versao }}
        ({{ t('plano') }} {{ termo?.plano_nome }} · {{ t('idioma') }} {{ termo?.language }})
      </p>

      <div class="consent-body" data-testid="subscription-terms-consent-body" v-html="sanitizedBody"></div>

      <label class="consent-check">
        <Toggle
          v-model="agreed"
          data-testid="subscription-terms-consent-agree"
        />
        <span>{{ t('Li e concordo com os termos acima.') }}</span>
      </label>

      <input-signature
        v-model="signature"
        elem-id="subscription-terms-signature"
        :label="t('Assinatura')"
        :required="true"
        data-testid="subscription-terms-signature"
        @change="handleSignatureChange"
      />

      <p v-if="errorCode" class="consent-error" data-testid="subscription-terms-consent-error">
        <i class="pi pi-exclamation-triangle"></i>
        {{ humanizeRefusal(errorCode) }}
      </p>
    </template>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { log } from '../../utils/DebugLogger'
import { sanitizeRichText } from '../../utils/sanitizeRichText'
import InputSignature from '../../inputs/InputSignature.vue'
import { useSubscriptionTerms } from '../../composables/useSubscriptionTerms'
import type { SubscriptionTerms_CurrentResponseDados } from '../../contracts/SubscriptionTerms_CurrentResponse'

type ConsentState = 'idle' | 'pending' | 'accepted' | 'nothing-to-sign' | 'error'

const props = defineProps<{
  franquiaId: number | null
  planId: number | null
}>()

const emit = defineEmits<{
  /** True when subscribing may proceed: signed now, signed before, or nothing to sign. */
  'update:ready': [ready: boolean]
}>()

const { t } = useI18n()
const { currentTerm, acceptTerm } = useSubscriptionTerms()

const state = ref<ConsentState>('idle')
const isLoading = ref(false)
const termo = ref<SubscriptionTerms_CurrentResponseDados | null>(null)
const agreed = ref(false)
const signature = ref('')
const signatureMethod = ref<'desenhada' | 'digitada'>('desenhada')
const signaturePoints = ref<number | null>(null)
const signatureMs = ref<number | null>(null)
const errorCode = ref<string | null>(null)

/**
 * Refusals that mean "there is no agreement to sign here", as opposed to
 * "something went wrong". Both let the franchisee continue: neither is their
 * problem to solve, and neither is a reason to strand them.
 */
const NOTHING_TO_SIGN = new Set(['termos_indisponiveis_para_o_plano'])

const acceptedAt = computed(() => termo.value?.accepted_at ?? '')

/**
 * The agreement body is author-written HTML from the staff editor, cleaned by
 * the platform's ALLOWLIST sanitiser before it is rendered. This text is shown
 * to every franchisee at the moment they sign, so a stored document must not be
 * able to run anything on that screen.
 */
const sanitizedBody = computed(() => sanitizeRichText(termo.value?.conteudo ?? ''))

const isReady = computed(() => {
  if (state.value === 'accepted' || state.value === 'nothing-to-sign') return true
  if (state.value !== 'pending') return false

  return agreed.value && signature.value !== ''
})

watch(isReady, (ready) => emit('update:ready', ready), { immediate: true })

function humanizeRefusal(code: string): string {
  const map: Record<string, string> = {
    termo_nao_esta_ativo: t('Estes termos foram substituídos. Recarregue a página para ver a versão em vigor.'),
    plano_nao_encontrado: t('Plano não encontrado — recarregue a página.'),
    assinatura_ausente: t('Assine no campo acima antes de continuar.'),
    assinatura_insuficiente: t('O traço registrado é curto demais. Assine novamente.'),
    assinatura_formato_invalido: t('Não foi possível ler a assinatura. Limpe e assine novamente.'),
    assinatura_muito_grande: t('A assinatura ficou grande demais. Limpe e assine novamente.'),
  }

  return map[code] ?? code
}

function retry(): void {
  if (props.franquiaId != null && props.planId != null) {
    void load(props.franquiaId, props.planId)
  }
}

function handleSignatureChange(payload: {
  value: string
  method: 'desenhada' | 'digitada'
  metrics: { points: number; durationMs: number }
}): void {
  signatureMethod.value = payload.method
  signaturePoints.value = payload.metrics.points
  signatureMs.value = payload.metrics.durationMs
}

async function load(franquiaId: number, planId: number): Promise<void> {
  isLoading.value = true
  errorCode.value = null
  agreed.value = false
  signature.value = ''

  try {
    const dados = await currentTerm(franquiaId, planId)
    termo.value = dados
    state.value = dados.accepted ? 'accepted' : 'pending'
  } catch (err) {
    const code = err instanceof Error ? err.message : String(err)
    if (NOTHING_TO_SIGN.has(code)) {
      state.value = 'nothing-to-sign'
      termo.value = null
      log('SubscriptionTermsConsent', 'load', 'Sem termo publicado — não bloqueia', code)
    } else {
      // Could not read the agreement. Block, but say so and offer a retry.
      //
      // Failing OPEN here would be worse than it looks: the server gate would
      // then refuse subscribe() with 'termos_nao_aceitos', and the franchisee
      // would be told they must sign something the screen never managed to
      // show them.
      state.value = 'error'
      errorCode.value = code
      log('SubscriptionTermsConsent', 'load', 'Falha ao carregar termos', code)
    }
  } finally {
    isLoading.value = false
  }
}

/**
 * Sign. Called by the subscribe form BEFORE it calls subscribe(), because the
 * backend gate refuses an unsigned agreement.
 *
 * Returns true when subscribing may proceed — including the cases where there
 * was nothing to sign or it was already signed.
 */
async function sign(): Promise<boolean> {
  if (state.value !== 'pending') return true
  if (props.franquiaId == null || props.planId == null || termo.value == null) return true

  errorCode.value = null

  try {
    await acceptTerm({
      franquia_id: props.franquiaId,
      term_id: termo.value.term_id,
      assinatura_png: signature.value,
      assinatura_metodo: signatureMethod.value,
      ...(signaturePoints.value != null ? { assinatura_pontos: signaturePoints.value } : {}),
      ...(signatureMs.value != null ? { assinatura_ms: signatureMs.value } : {}),
    })
    state.value = 'accepted'
    return true
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
    log('SubscriptionTermsConsent', 'sign', 'Recusa', errorCode.value)
    return false
  }
}

// The agreement hangs off the PLAN, so changing either the franchise or the
// plan changes which text has to be signed. Watching only the franchise would
// leave the previous plan's agreement on screen after the picker moved.
watch(
  [() => props.franquiaId, () => props.planId],
  ([franquiaId, planId]) => {
    if (franquiaId == null || planId == null || planId <= 0) {
      state.value = 'idle'
      termo.value = null
      return
    }
    void load(franquiaId, planId)
  },
  { immediate: true },
)

defineExpose({ sign, isReady })
</script>

<style scoped>
.consent-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.consent-meta {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--p-text-muted-color, #6b7280);
}

.consent-body {
  max-height: 240px;
  overflow-y: auto;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.55;
}

.consent-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.consent-accepted {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--p-green-600, #16a34a);
  font-size: 14px;
}

.consent-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--p-red-500);
  font-size: 13px;
  margin: 8px 0 0;
}
</style>
